/**
 * Shared navigation styling tokens.
 *
 * These string constants are the single source of truth for nav link
 * typography, spacing, dividers, state styling (base / hover / active /
 * pressed), and focus rings. Compose them — never inline a one-off color or
 * focus utility on a nav-style link in any component.
 *
 * Usage:
 *   import { desktopNavLink, desktopNavLinkActive, NAV_FOCUS_RING } from "@/styles/nav";
 *
 *   <Link className={desktopNavLink} activeProps={{ className: desktopNavLinkActive }} />
 */

// ---------------------------------------------------------------------------
// Typography + spacing
// ---------------------------------------------------------------------------
export const NAV_TYPOGRAPHY_DESKTOP =
  "font-display text-xs uppercase tracking-wider xl:text-sm";
export const NAV_TYPOGRAPHY_MOBILE =
  "font-display text-base uppercase tracking-wider";

export const NAV_ITEM_SPACING_DESKTOP = "px-3 py-3 xl:px-4";
export const NAV_ITEM_SPACING_MOBILE = "px-3 py-3.5";

/**
 * Layout for nav-item content boxes.
 *
 * Why these are pinned (not just an `inline-block` default):
 *   - `inline-flex items-center justify-center text-center` keeps single-line
 *     labels (HOME, AWNINGS, FABRICS) baseline-aligned with multi-line labels
 *     (POOL & SPA, COVERS & TOPS, SAIL SHADES, AG / TRAILER). Without an
 *     explicit cross-axis center, the shorter labels sit at flex-start and
 *     drift up by ~6–8px on rows where wrapping kicks in.
 *   - `min-h-[3.25rem]` (desktop) / `min-h-[2.5rem]` (mobile) reserves the
 *     row height that the tallest two-line label needs. This prevents the
 *     row height from collapsing/expanding when the user navigates between
 *     pages (active item rendered in a different position) or when the
 *     desktop container's gap is changed (no implicit gap means link boxes
 *     touch — they MUST be the same height to render the divider gutter
 *     cleanly without a stair-step).
 *   - `whitespace-normal` lets the multi-line labels wrap as designed; the
 *     min-height reservation absorbs the extra line.
 */
export const NAV_ITEM_BOX_DESKTOP =
  "inline-flex min-h-[3.25rem] items-center justify-center text-center whitespace-normal leading-tight";
export const NAV_ITEM_BOX_MOBILE =
  "inline-flex min-h-[3rem] items-center justify-start text-left whitespace-normal leading-tight";

// ---------------------------------------------------------------------------
// State tokens (shared across desktop + mobile + external links)
// ---------------------------------------------------------------------------
export const NAV_BASE = "text-white/85";
export const NAV_HOVER =
  "transition-colors duration-200 ease-out motion-reduce:transition-none motion-reduce:duration-0 hover:bg-red hover:text-white";
/** Route-active state (current page). */
export const NAV_ACTIVE = "bg-red text-white hover:bg-red-deep";
/** :active pseudo (mouse/touch press). */
export const NAV_PRESSED = "active:bg-red-deep active:text-white";

// ---------------------------------------------------------------------------
// Focus rings — context-specific
// ---------------------------------------------------------------------------
/** Focus ring for elements on the dark navy nav surface. */
export const NAV_FOCUS_RING =
  "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep";
/** Focus ring for elements on the light white topbar surface. */
export const TOPBAR_FOCUS_RING =
  "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2 focus-visible:ring-offset-white";

// ---------------------------------------------------------------------------
// Dividers
//
// Implementation note (a11y):
//   The divider is rendered as a decorative ::before pseudo-element sitting
//   in the gutter of each item — NOT as a real border on the link's box.
//   Why:
//     1. Focus rings (ring-2 + ring-offset-2) wrap the focusable box.
//        If the divider were a `border-l` on the box, the focus ring would
//        share an edge with a tinted border, producing a visually muddy or
//        clipped outline. With a pseudo-element, the link's box has no
//        border, so the focus ring is crisp on every side.
//     2. Pseudo-elements are absent from the a11y tree, so screen readers
//        and keyboard users perceive a clean list of links — no phantom
//        "border" element is announced or tabbed to.
//     3. The active red surface no longer needs a "transparent border" hack
//        to suppress a tinted seam — the divider simply doesn't render on
//        the active item.
//
// Required scaffolding on the parent: items must be `relative` so the
// absolutely-positioned ::before anchors to each item's gutter. The item
// must NOT use `overflow-hidden` (which would clip both the divider and
// the focus ring's offset).
// ---------------------------------------------------------------------------
export const NAV_DIVIDER_COLOR_VAR = "bg-white/25";

/**
 * Decorative left-edge divider for desktop nav rows.
 * - Inset top/bottom so the divider reads as a separator, not an edge stroke.
 * - `first:before:hidden` suppresses the leading edge.
 * - `aria-[current]:before:hidden` suppresses the divider on the
 *   currently-active item so the red surface fills cleanly.
 */
export const NAV_DIVIDER_DESKTOP = [
  "relative",
  "before:pointer-events-none before:absolute before:left-0 before:top-2 before:bottom-2 before:w-px",
  `before:${NAV_DIVIDER_COLOR_VAR}`,
  "before:content-['']",
  "first:before:hidden",
  "aria-[current]:before:hidden",
].join(" ");

/**
 * Decorative bottom-edge divider for mobile menu rows.
 * - Inset left/right so the line reads as an internal separator.
 * - `last:before:hidden` suppresses the trailing edge.
 * - Active item suppresses its own divider; sibling above still shows theirs.
 */
export const NAV_DIVIDER_MOBILE = [
  "relative",
  "before:pointer-events-none before:absolute before:left-2 before:right-2 before:bottom-0 before:h-px",
  `before:${NAV_DIVIDER_COLOR_VAR}`,
  "before:content-['']",
  "last:before:hidden",
  "aria-[current]:before:hidden",
].join(" ");

// ---------------------------------------------------------------------------
// Composed link variants
//
// Base + active variants share the same divider scaffolding (relative + ::before),
// spacing, typography, pressed, and focus tokens. Only the surface color tokens
// differ. The divider's visibility is driven by `aria-current="page"` (set via
// TanStack Router's activeProps), so a single class set covers both states.
// ---------------------------------------------------------------------------
const desktopNavLinkShared = `${NAV_DIVIDER_DESKTOP} ${NAV_ITEM_BOX_DESKTOP} ${NAV_ITEM_SPACING_DESKTOP} ${NAV_TYPOGRAPHY_DESKTOP} ${NAV_PRESSED} ${NAV_FOCUS_RING}`;
export const desktopNavLink = `${desktopNavLinkShared} ${NAV_BASE} ${NAV_HOVER}`;
export const desktopNavLinkActive = `${desktopNavLinkShared} ${NAV_ACTIVE}`;
export const desktopFbLink = `inline-flex items-center gap-2 ${desktopNavLink}`;
/** Active variant for the desktop FB link — applied when the user has visited it before. */
export const desktopFbLinkActive = `inline-flex items-center gap-2 ${desktopNavLinkActive}`;

const mobileNavLinkShared = `${NAV_DIVIDER_MOBILE} ${NAV_ITEM_BOX_MOBILE} ${NAV_ITEM_SPACING_MOBILE} ${NAV_TYPOGRAPHY_MOBILE} ${NAV_PRESSED} ${NAV_FOCUS_RING}`;
export const mobileNavLink = `${mobileNavLinkShared} text-white hover:text-white ${NAV_HOVER}`;
export const mobileNavLinkActive = `${mobileNavLinkShared} ${NAV_ACTIVE}`;
/**
 * Layout shared by base + active mobile FB link variants.
 * The FB link inherits the same ::before divider scaffolding for visual parity
 * with mobile nav rows.
 */
const mobileFbLinkLayout = `mt-2 inline-flex items-center justify-center gap-2 ${mobileNavLinkShared}`;
export const mobileFbLink = `${mobileFbLinkLayout} text-white ${NAV_HOVER}`;
/** Active variant for the mobile FB link — divider auto-hides via aria-current. */
export const mobileFbLinkActive = `${mobileFbLinkLayout} ${NAV_ACTIVE}`;
